'use client';

import type { FC } from 'react';
import { useMemo, useRef } from 'react';
import { BottomSheet, EmptyState, Skeleton, Spinner } from '../../components/ui';
import { SkinImage } from '../../components/ui/SkinImage';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Credits } from '../../components/Credits';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { formatDateNumeric } from '../../i18n/formatDate';
import { useContractHistoryInfinite } from '../../queries/useContractQueries';
import type { ContractHistoryItem } from '../../services/api';
import { extractSkinName } from '../../utils/formatters';
import { cn } from '../../utils/cn';
import translations from './ContractScreen.translations';

interface ContractHistorySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const VISIBLE_INPUT_THUMBS = 3;

const Thumb: FC<{ name: string; src: string | null }> = ({ name, src }) => (
  <span className="relative block h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-white/[0.06]">
    <SkinImage name={name} src={src} imgClassName="h-full w-full object-contain" />
  </span>
);

const HistoryRow: FC<{ item: ContractHistoryItem; lang: Parameters<typeof formatDateNumeric>[1] }> = ({
  item,
  lang,
}) => {
  const hiddenInputs = item.inputItems.length - VISIBLE_INPUT_THUMBS;

  return (
    <li
      data-testid="contract-history-row"
      className="flex w-full items-center gap-2 border-b border-white/5 py-3 last:border-b-0"
    >
      <span className="flex shrink-0 items-center -space-x-4">
        {item.inputItems.slice(0, VISIBLE_INPUT_THUMBS).map((input) => (
          <Thumb key={input.id} name={input.name} src={input.imageUrl} />
        ))}
        {hiddenInputs > 0 && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-[11px] font-semibold text-white/60">
            +{hiddenInputs}
          </span>
        )}
      </span>
      <span aria-hidden className="shrink-0 text-white/30">→</span>
      <Thumb name={item.outputItem.name} src={item.outputItem.imageUrl} />
      <div className="min-w-0 flex-1 overflow-hidden ps-1">
        <p className="m-0 truncate text-sm font-semibold text-white/90">{extractSkinName(item.outputItem.name)}</p>
        <p className="m-0 mt-0.5 truncate text-[11px] text-white/40">
          {formatDateNumeric(item.createdAt, lang, { time: true })}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end text-xs">
        <Credits amount={item.outputItem.value} iconSize={11} className="font-semibold text-white" />
        <span className={cn('font-semibold', item.profitLoss >= 0 ? 'text-accent-green' : 'text-accent-red')}>
          {item.profitLoss >= 0 ? '+' : '−'}
          <Credits amount={Math.abs(item.profitLoss)} iconSize={10} />
        </span>
      </div>
    </li>
  );
};

export const ContractHistorySheet: FC<ContractHistorySheetProps> = ({ isOpen, onClose }) => {
  const { t, lang } = useLocalTranslation(translations);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading, isError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useContractHistoryInfinite(isOpen);

  const items = useMemo(() => data?.pages.flatMap((page) => page.contracts) ?? [], [data]);

  useInfiniteScroll({
    hasMore: hasNextPage,
    loading: isFetchingNextPage,
    onLoadMore: () => void fetchNextPage(),
    threshold: 200,
    scrollRef,
  });

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('history')} scrollRef={scrollRef}>
      {isLoading ? (
        <div className="flex flex-col gap-3 px-4 py-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <ErrorMessage error={t('errorLoadHistory')} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={t('historyEmpty')} />
      ) : (
        <>
          <ul className="m-0 list-none px-4 py-1">
            {items.map((item) => (
              <HistoryRow key={item.id} item={item} lang={lang} />
            ))}
          </ul>
          {isFetchingNextPage && (
            <div className="flex justify-center p-5">
              <Spinner size="s" />
            </div>
          )}
        </>
      )}
    </BottomSheet>
  );
};
